import { z } from "zod";

import {
  firstConversationShopCandidate,
  workspaceConversationPath,
} from "@/features/chat/conversationShopScope";

export const notificationCategories = [
  "conversation_task",
  "approval_execution",
  "store_connection",
  "report_file",
  "billing_resource",
  "team_invitation",
] as const;

export const notificationCategorySchema = z.enum(notificationCategories);

const notificationShopBindingIdSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9:._-]{0,159}$/u);

const conversationNotificationActionWireSchema = <
  Kind extends "open_approval" | "open_conversation",
>(
  kind: Kind,
) =>
  z
    .object({
      kind: z.literal(kind),
      resource_id: z.string().min(1),
      shop_binding_id: notificationShopBindingIdSchema.optional(),
    })
    .strict();

const notificationActionWireSchema = z
  .discriminatedUnion("kind", [
    conversationNotificationActionWireSchema("open_conversation"),
    conversationNotificationActionWireSchema("open_approval"),
    z.object({ kind: z.literal("open_connections"), resource_id: z.string() }).strict(),
    z.object({ kind: z.literal("open_stores"), resource_id: z.string() }).strict(),
    z.object({ kind: z.literal("open_files"), resource_id: z.string() }).strict(),
    z.object({ kind: z.literal("open_billing"), resource_id: z.string() }).strict(),
    z.object({ kind: z.literal("open_team"), resource_id: z.string() }).strict(),
    z.object({
      kind: z.literal("open_scheduled_tasks"),
      resource_id: z.string().regex(/^mst_[a-z0-9]+$/u),
    }).strict(),
  ])
  .transform((action) => {
    if (action.kind === "open_conversation" || action.kind === "open_approval") {
      return {
        kind: action.kind,
        resourceId: action.resource_id,
        shopBindingId: action.shop_binding_id ?? "",
      };
    }
    return { kind: action.kind, resourceId: action.resource_id };
  });

const notificationWireSchema = z
  .object({
    action: notificationActionWireSchema.nullable(),
    category: notificationCategorySchema,
    content_revision: z.number().int().positive(),
    id: z.string().min(1),
    occurred_at: z.string().datetime({ offset: true }),
    read_at: z.string().datetime({ offset: true }).nullable(),
    read_revision: z.number().int().positive(),
    summary: z.string().max(280),
    title: z.string().min(1).max(120),
    workflow: z.object({
      revision: z.number().int().positive(),
      status: z.enum(["running", "waiting_approval", "succeeded", "partial_failed", "failed", "cancelled"]),
      workflow_id: z.string().min(1).max(160),
    }).strict().nullable().default(null),
  })
  .strict()
  .transform((notification) => ({
    action: notification.action,
    category: notification.category,
    contentRevision: notification.content_revision,
    id: notification.id,
    occurredAt: notification.occurred_at,
    readAt: notification.read_at,
    readRevision: notification.read_revision,
    summary: notification.summary,
    title: notification.title,
    workflow: notification.workflow
      ? {
          id: notification.workflow.workflow_id,
          revision: notification.workflow.revision,
          status: notification.workflow.status,
        }
      : null,
  }));

export const notificationListResponseSchema = z
  .object({
    items: z.array(notificationWireSchema),
    next_cursor: z.string(),
    revision: z.number().int().positive(),
    unread_count: z.number().int().nonnegative(),
  })
  .strict()
  .transform((response) => ({
    items: response.items,
    nextCursor: response.next_cursor,
    revision: response.revision,
    unreadCount: response.unread_count,
  }));

export const notificationSummaryResponseSchema = z
  .object({
    items: z.array(notificationWireSchema),
    revision: z.number().int().positive(),
    unread_count: z.number().int().nonnegative(),
  })
  .strict()
  .transform((response) => ({
    items: response.items,
    revision: response.revision,
    unreadCount: response.unread_count,
  }));

export const notificationReadResponseSchema = z
  .object({
    notification: notificationWireSchema,
    revision: z.number().int().positive(),
    unread_count: z.number().int().nonnegative(),
  })
  .strict()
  .transform((response) => ({
    notification: response.notification,
    revision: response.revision,
    unreadCount: response.unread_count,
  }));

export const notificationReadAllResponseSchema = z
  .object({
    revision: z.number().int().positive(),
    unread_count: z.number().int().nonnegative(),
  })
  .strict()
  .transform((response) => ({
    revision: response.revision,
    unreadCount: response.unread_count,
  }));

export type UserNotification = z.infer<typeof notificationWireSchema>;
export type NotificationCategory = z.infer<typeof notificationCategorySchema>;
export type NotificationAction = NonNullable<UserNotification["action"]>;

export function resolveNotificationConversationShop(
  action: NotificationAction,
  ...fallbacks: Array<string | null | undefined>
) {
  if (action.kind !== "open_conversation" && action.kind !== "open_approval") {
    return "";
  }
  return firstConversationShopCandidate(action.shopBindingId, ...fallbacks);
}

export function getNotificationActionPath(
  action: NotificationAction,
  shopBindingId = "",
) {
  switch (action.kind) {
    case "open_conversation":
    case "open_approval":
      return workspaceConversationPath(
        action.resourceId,
        firstConversationShopCandidate(action.shopBindingId, shopBindingId),
      );
    case "open_connections":
      return "/settings/connections";
    case "open_stores":
      return "/settings/stores";
    case "open_files": {
      const params = new URLSearchParams({ drawer: "my-files" });
      if (action.resourceId) params.set("resource", action.resourceId);
      return `/workspace?${params.toString()}`;
    }
    case "open_billing":
      return "/settings/billing";
    case "open_team":
      return "/settings/team";
    case "open_scheduled_tasks": {
      const params = new URLSearchParams({ task: action.resourceId });
      return `/settings/tasks?${params.toString()}`;
    }
  }
}
